import { config } from "@config/config";
import { redactErrorVariables } from "@utils/redactErrorVariables";
import { Variables } from "graphql-request";

import { MultipartGraphQLClient } from "@api/graphql/MultipartGraphQLClient";
import { useAuthorization } from "@api/useAuthorization.tsx";

const endpoint = config.endPoint;

export const FronteraGraphQLClient = new MultipartGraphQLClient(endpoint);

// Set to store active request
const activeRequests = new Map<string, { request: Promise<unknown>; isMutation: boolean }>();

export const useCustomFetcher = <TData, TVariables extends Variables>(
  query: string,
): ((variables?: TVariables) => Promise<TData>) => {
  const { getAuthorizationHeader, handleApiError } = useAuthorization();

  return async (variables?: TVariables) => {
    const requestKey = `${query}-${JSON.stringify(variables || {})}`;
    const isMutation = /^\s*mutation\b/.test(query);

    try {
      const activeRequest = activeRequests.get(requestKey);
      if (activeRequest) {
        return await activeRequest.request;
      }

      const headers = await getAuthorizationHeader();
      const request = FronteraGraphQLClient.request({
        document: query,
        variables,
        requestHeaders: headers,
      });
      activeRequests.set(requestKey, { request, isMutation });

      return await request;
    } catch (err) {
      // Before handleApiError, which logs the error object wholesale.
      redactErrorVariables(err);
      throw await handleApiError(err as Error, query);
    } finally {
      activeRequests.delete(requestKey);
      if (isMutation) {
        for (const [key, entry] of activeRequests) {
          if (!entry.isMutation) activeRequests.delete(key);
        }
      }
    }
  };
};
